import React, { useMemo } from 'react';
import {
  createNavigationContainerRef,
  DocumentTitleOptions,
  LinkingOptions,
  NavigationContainer,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useModal } from 'react-native-modalfy';

import { screens, ScreensNameType } from './screens';
import { SwitchNavigator } from './SwitchNavigator';
import { AppStackParamListType } from './types';
import { linking } from './linking';
import { FormPickerScreen, PaymentConfirmScreen } from '@screens';
import { Header } from '@components';
import { useAsyncInit, useDeepLinking, useVar } from '../hooks';
import { useAuthModel } from '@models/AuthModel';
import { IDeepLinkingContext } from '@context';
import { useAppModel } from '@models/App/AppModel';
import { delay, isMobileBrowser } from '@utils';
import { modals, ModalStackParamsType } from '@modals';
import {
  AnalyticsService,
  InAppMessagingService,
  getFormerScreenNameForAnalytics,
} from '@services';

export const navigationRef =
  createNavigationContainerRef<AppStackParamListType>();

const AppStack = createNativeStackNavigator<AppStackParamListType>();

const documentTitleOptions: DocumentTitleOptions = {
  formatter: (options, route) =>
    `${options?.title ?? route?.name} | Syntho`,
};

type DefineUrlPropsType = Partial<
  Pick<IDeepLinkingContext, 'openedUrl' | 'initialUrl'>
>;

export const AppNavigator = observer(() => {
  let routeName = useVar<string>('');
  const { openModal } = useModal<ModalStackParamsType>();
  const deepLinking = useDeepLinking();
  const app = useAppModel();
  const auth = useAuthModel();

  useAsyncInit(async () => {
    showMobileAppSuggestion();
    app.updateLaunchDate();
    trackLastSeenDateAnalytic();
  });

  /**
   * When launching app from the mobile browser on the first launch
   * should open suggestion to download mobile app for better view
   * also delayed for 1s in order to improve user experience
   */
  function showMobileAppSuggestion() {
    if (!app.hasEverLaunched && isMobileBrowser()) {
      delay(1000).then(() => {
        openModal(modals.MobileAppSuggestionModal);
      });
    }
  }

  /**
   * 'lastSeenDate' - date of the last time the user was active in the app
   * 'launchDate' - date the app was opened by the user; it is updated each time the app is launched
   *  We are using launchDate for lastSeenDate in analytics because it is a reliable indicator of when the user last interacted with the app.
   *  It is better to track it on mount because we can guarantee it will be sent to analytics
   */
  function trackLastSeenDateAnalytic() {
    if (app.launchDate) {
      AnalyticsService.trackUserParams({
        lastSeenDate: app.launchDate,
      });
    }
  }

  function trackScreenViewAnalytic(name: string) {
    if (name.length > 0) {
      AnalyticsService.trackScreenView(name as ScreensNameType);
    }
  }

  function getCurrentRouteName() {
    const route = navigationRef.current?.getCurrentRoute();

    if (route) {
      return getFormerScreenNameForAnalytics(route);
    }

    return '';
  }

  function defineUrl({ openedUrl, initialUrl }: DefineUrlPropsType) {
    // For Authorized user is no need to store url
    if (auth.isAuthorized) {
      return;
    } else if (openedUrl) {
      deepLinking.setOpenedUrl(openedUrl);
    } else if (initialUrl) {
      deepLinking.setInitialUrl(initialUrl);
    }
  }

  function onReady() {
    routeName = getCurrentRouteName();
    trackScreenViewAnalytic(routeName);
    InAppMessagingService.subscribe();
  }

  function onStateChange() {
    const currentRouteName = getCurrentRouteName();

    if (currentRouteName && currentRouteName !== routeName) {
      trackScreenViewAnalytic(currentRouteName);
    }

    // Save the current route name for later comparison
    routeName = currentRouteName;
  }

  // Methods below 'getInitialURL' and 'subscribe'
  // are not supported on Web.
  const linkingParams = useMemo<
    LinkingOptions<ReactNavigation.RootParamList>
  >(
    () => ({
      ...linking,
      async getInitialURL() {
        const initialUrl = await linking.getInitialURL?.();

        // We must wait until defining authorization status because for not defined
        // status we are using the initial empty screen instead of mounting
        // Auth and Main navigators and before continuing to navigate inside those
        // navigators we need to wait until they mount
        await when(() => typeof auth.isAuthorized === 'boolean');
        defineUrl({ initialUrl });

        return initialUrl;
      },
      subscribe(listener) {
        return linking.subscribe?.((openedUrl) => {
          listener(openedUrl);
          defineUrl({ openedUrl });
        });
      },
    }),
    [],
  );

  return (
    <NavigationContainer<AppStackParamListType>
      ref={navigationRef}
      onReady={onReady}
      onStateChange={onStateChange}
      linking={linkingParams}
      documentTitle={documentTitleOptions}
    >
      <AppStack.Navigator screenOptions={{ headerShown: false }}>
        <AppStack.Screen
          name={screens.SwitchNavigator}
          component={SwitchNavigator}
        />
        <AppStack.Screen
          component={FormPickerScreen}
          name={screens.FormPicker}
          options={{
            headerShown: true,
            header: (props) => <Header {...props} />,
          }}
        />
        <AppStack.Screen
          component={PaymentConfirmScreen}
          name={screens.PaymentConfirm}
          options={{
            headerShown: false,
          }}
        />
      </AppStack.Navigator>
    </NavigationContainer>
  );
});
